import { Treemap, ResponsiveContainer, Tooltip } from "recharts";
import type { BiCategoryRow } from "@/resources/brand-intelligence";

// Section 3.1: revenue treemap. Tile size = revenue (brand or category view). Fill is a
// monochrome PRIMARY (blue) intensity ramp by relative size, matching the signal-v3
// prototype (larger tiles lighter). The §2.7 health tier is shown in the table's Status
// column + Brand Visibility ring rather than tinting the tiles. Click a tile to filter.

export type TreemapView = "brand" | "category";

const fmtUsd = (n: number): string => {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

// AUT-306: a centered label wider than its tile used to spill over adjacent tiles and
// garble. Truncate to what the tile width can actually hold (~6.5px per glyph at fontSize
// 12, 8px horizontal padding); the tile is also clipped so nothing can ever overflow.
export function fitTreemapLabel(name: string, width: number): string {
  const maxChars = Math.floor((width - 8) / 6.5);
  if (maxChars < 2) return "";
  return name.length > maxChars ? `${name.slice(0, Math.max(1, maxChars - 1))}…` : name;
}

interface TreemapNode {
  /** recharts' own field: 0 is the synthesized root, 1 is a real category tile. */
  depth?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  size?: number;
  share?: number;
  frac?: number;
  health?: string | null;
  leaf?: string;
  selected?: boolean;
  dimmed?: boolean;
  onTile?: (leaf: string) => void;
}

function TreemapCell(props: TreemapNode) {
  const { depth, x = 0, y = 0, width = 0, height = 0, name = "", size = 0, share, frac = 0.5, leaf = "", selected, dimmed, onTile } = props;
  if (width <= 0 || height <= 0) return null;
  // AUT-360: recharts wraps a flat `data` array in a synthesized ROOT node and renders it through
  // this same `content` renderer. That root spans the entire chart and carries no `size`, so the
  // value line below printed `fmtUsd(0)` -- a stray "$0" dead centre of both the Brand revenue and
  // Category revenue views, over the tiles. Only leaves are tiles. Guarded on `leaf` as well as
  // `depth`, since every real row is built with a `leaf_category_id` and the root has none.
  if (depth === 0 || !leaf) return null;
  const showLabel = width > 46 && height > 30;
  const showValue = width > 92 && height > 54;
  const showShare = showValue && share != null && height > 72;
  const cx = x + width / 2;
  const label = fitTreemapLabel(name, width);
  // Clip every label to its own tile so text can never render over an adjacent tile (AUT-306).
  // Sanitize the leaf into the id: leaf_category_id is numeric in prod but a free-text name in
  // some environments, and spaces/punctuation would make an invalid SVG id + url(#...) ref.
  const clipId = `bi-tm-${String(leaf).replace(/[^a-zA-Z0-9_-]/g, "")}-${Math.round(x)}-${Math.round(y)}`;
  // Monochrome primary ramp: larger tiles lighter (0.45 -> 0.95 opacity).
  const baseOpacity = 0.45 + 0.5 * Math.min(1, frac);
  return (
    <g style={{ cursor: onTile ? "pointer" : "default" }} onClick={() => onTile?.(leaf)}>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{ fill: "hsl(var(--primary))", fillOpacity: dimmed ? 0.2 : selected ? 1 : baseOpacity, stroke: "none", transition: "fill-opacity 120ms ease" }}
      />
      <clipPath id={clipId}>
        <rect x={x} y={y} width={width} height={height} />
      </clipPath>
      {showLabel && label && (
        <text x={cx} y={y + height / 2 - (showValue ? 8 : 0)} textAnchor="middle" fontSize={12} fontWeight={500} fill="hsl(var(--primary-foreground))" clipPath={`url(#${clipId})`} className="pointer-events-none">
          {label}
        </text>
      )}
      {showValue && (
        <text x={cx} y={y + height / 2 + 8} textAnchor="middle" fontSize={11} fillOpacity={0.9} fill="hsl(var(--primary-foreground))" clipPath={`url(#${clipId})`} className="pointer-events-none">
          {fmtUsd(size)}
        </text>
      )}
      {showShare && (
        <text x={cx} y={y + height / 2 + 22} textAnchor="middle" fontSize={10} fillOpacity={0.7} fill="hsl(var(--primary-foreground))" clipPath={`url(#${clipId})`} className="pointer-events-none">
          {share!.toFixed(0)}% of total
        </text>
      )}
    </g>
  );
}

function TreemapTooltip({ active, payload }: { active?: boolean; payload?: { payload: TreemapNode }[] }) {
  if (!active || !payload?.length) return null;
  const node = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <div className="font-semibold mb-1">{node.name}</div>
      <div className="flex justify-between gap-4">
        <span className="text-muted-foreground">Revenue</span>
        <span className="font-semibold tabular-nums">{fmtUsd(node.size ?? 0)}</span>
      </div>
      {node.share != null && (
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Share of total</span>
          <span className="font-semibold tabular-nums">{node.share.toFixed(1)}%</span>
        </div>
      )}
      {node.health && (
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Status</span>
          <span className="font-semibold">{node.health}</span>
        </div>
      )}
    </div>
  );
}

export function RevenueTreemap({
  categories,
  view,
  selected,
  onTile,
}: {
  categories: BiCategoryRow[];
  view: TreemapView;
  selected?: Set<string>;
  onTile?: (leaf: string) => void;
}) {
  const rows = categories
    .map((c) => ({ ...c, size: view === "brand" ? c.brand_revenue : c.category_revenue }))
    .filter((d) => d.size > 0);
  const total = rows.reduce((s, r) => s + r.size, 0);
  const max = Math.max(1, ...rows.map((r) => r.size));
  const hasSel = (selected?.size ?? 0) > 0;

  const data = rows.map((c) => ({
    name: c.name,
    size: c.size,
    share: total > 0 ? (c.size / total) * 100 : 0,
    frac: c.size / max,
    health: c.health_status,
    leaf: c.leaf_category_id,
    selected: selected?.has(c.leaf_category_id),
    dimmed: hasSel && !selected?.has(c.leaf_category_id),
    onTile,
  }));

  if (data.length === 0) {
    // AUT-323: the treemap empties when no shown category has revenue > 0 in this view — not
    // because a filter excluded results. Name the real cause (and the correct revenue dimension).
    return (
      <div className="h-[380px] w-full flex items-center justify-center rounded-sm border border-dashed border-border text-sm text-muted-foreground">
        {view === "brand"
          ? "No brand revenue to visualize for the categories shown"
          : "No category revenue to visualize for the categories shown"}
      </div>
    );
  }

  return (
    <div className="h-[380px] w-full bg-background">
      <ResponsiveContainer width="100%" height="100%">
        <Treemap data={data} dataKey="size" nameKey="name" stroke="none" content={<TreemapCell />} isAnimationActive={false}>
          <Tooltip content={<TreemapTooltip />} />
        </Treemap>
      </ResponsiveContainer>
    </div>
  );
}
